// Copyright (c) 2026 GLOBAL MICRO IT TECHNOLOGY SOLUTIONS LIMITED
// Company No. 758762, Dublin, Ireland
// All rights reserved. Proprietary and confidential.

import { useState, useEffect, useMemo } from 'react';
import { type Theme } from '@fluentui/react-components';
import { gmsLightTheme, gmsDarkTheme, brandTokens } from '../theme/gms-brand';

export type ThemePreference = 'system' | 'light' | 'dark';

const DARK_MQ = '(prefers-color-scheme: dark)';

/**
 * Resolves a user's theme preference to a Fluent UI Theme object.
 * When preference is 'system', listens for OS colour-scheme changes in real time.
 * Also sets document-level hints (color-scheme, body background) to prevent flash.
 */
export function useResolvedTheme(preference: ThemePreference): Theme {
  const [osDark, setOsDark] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(DARK_MQ).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(DARK_MQ);
    const handler = (e: MediaQueryListEvent) => { setOsDark(e.matches); };
    mql.addEventListener('change', handler);
    // Sync in case it changed between render and effect
    setOsDark(mql.matches);
    return () => { mql.removeEventListener('change', handler); };
  }, []);

  const isDark =
    preference === 'dark' || (preference === 'system' && osDark);

  const theme = useMemo<Theme>(
    () => (isDark ? gmsDarkTheme : gmsLightTheme),
    [isDark],
  );

  // Keep document-level hints in sync to prevent body flash and style native scrollbars,
  // and emit the 4:2:1 brand token catalogue on :root so CSS can reference it by name
  // (raw hex is forbidden in *.css by the styling-consistency guard, so the catalogue
  // lives in gms-brand.ts and is applied here). `data-theme` is the selector the
  // Rebrand-421 mockups switch on.
  useEffect(() => {
    const root = document.documentElement;
    root.style.colorScheme = isDark ? 'dark' : 'light';
    root.dataset.theme = isDark ? 'dark' : 'light';
    const tokens = brandTokens(isDark);
    for (const [name, value] of Object.entries(tokens)) {
      root.style.setProperty(name, value);
    }
    document.body.style.backgroundColor = isDark
      ? gmsDarkTheme.colorNeutralBackground1
      : gmsLightTheme.colorNeutralBackground1;
  }, [isDark]);

  return theme;
}
